/**
 * Meeting point between TanStack Query's `MutationCache` and the interface.
 *
 * The cache lives outside the React tree and fires `onError` from inside a promise, with no
 * component around, so `QueryProvider` registers the publisher in an effect. Passing
 * `showToast` to the QueryClient constructor instead means reading a ref during render.
 */

type ErrorReporter = (message: string) => void;

let reporter: ErrorReporter | null = null;

/** Returns the deregistration, for the effect that registered to undo on unmount. */
export function setMutationErrorReporter(next: ErrorReporter): () => void {
  reporter = next;

  return () => {
    // Only deregisters while it is still the same one: two providers mounted in sequence must
    // not leave the app without a publisher.
    if (reporter === next) reporter = null;
  };
}

/**
 * Publishes a mutation failure.
 *
 * With no publisher registered (server render, or before the first effect) the message is
 * dropped: there is no surface to show it on, and throwing here would take down the operation
 * that already failed.
 */
export function reportMutationError(message: string): void {
  reporter?.(message);
}
